import { useCallback, useEffect, useRef, useState } from 'react';
import { isCustomSection } from '.';
import { EarconName, useEarcon } from '../../../hooks/useEarcon';
import { useIsRTL } from '../../../locale';

/**
 * @internal
 */

/**
 * Returns true if an item is focused
 * @internal
 */
export function anyItemHasFocus(focusLocation) {
  return focusLocation && focusLocation.length > 0 &&
  // Something has focus
  focusLocation[focusLocation.length - 1].length === 2 // Focus is on an item
  ;
}

/**
 * @internal
 */
export function useFocusNavigation(props) {
  const {
    sections,
    isOpen,
    onDismiss,
    defaultFocusLocation
  } = props;
  const {
    focusLocation,
    showFocus,
    onItemHovered,
    onItemLeaved,
    onDrillDown,
    onDrillUp,
    onPrevious,
    onNext,
    onHome,
    onEnd,
    onSubmenuClosed,
    forceShowFocus,
    onTypeAhead
  } = useFocusControls(sections);
  const earcon = useEarcon({
    source: EarconName.Error
  });
  const isRTL = useIsRTL();
  {
    // Using refs so the effect only runs when isOpen changes
    const onItemHoveredRef = useRef(onItemHovered);
    const defaultFocusLocationRef = useRef(defaultFocusLocation);
    useEffect(() => {
      onItemHoveredRef.current = onItemHovered;
    }, [onItemHovered]);
    useEffect(() => {
      defaultFocusLocationRef.current = defaultFocusLocation;
    }, [defaultFocusLocation]);
    useEffect(() => {
      if (isOpen && defaultFocusLocationRef.current) {
        // Focus the default item for menu button
        onItemHoveredRef.current([defaultFocusLocationRef.current]);
        forceShowFocus();
      } else {
        // When the menu opens or closes, reset the focus to null
        onItemHoveredRef.current(null);
      }
    }, [isOpen, forceShowFocus]);
  }
  const onKeyDown = useCallback(event => {
    if (!isOpen) return;
    switch (event.key) {
      case 'Enter':
      case ' ':
        if (!focusLocation) return earcon?.play();
        event.preventDefault();
        event.stopPropagation();
        if (canDrillDown(focusLocation, sections)) {
          onDrillDown();
        }
        return;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        if (focusLocation && focusLocation.length > 1) {
          return onDrillUp();
        }
        return onDismiss && onDismiss(true);
      case 'Tab':
        return onDismiss && onDismiss(true);
      case 'ArrowLeft':
        return isRTL ? onDrillDown() : onDrillUp();
      case 'ArrowUp':
        event.preventDefault();
        return onPrevious();
      case 'ArrowRight':
        return isRTL ? onDrillUp() : onDrillDown();
      case 'ArrowDown':
        event.preventDefault();
        return onNext();
      case 'Home':
        event.preventDefault();
        return onHome();
      case 'End':
        event.preventDefault();
        return onEnd();
      case 'Delete':
        if (!focusLocation) earcon?.play();
        return;
      default:
        if (event.key.length === 1) {
          // the printable character key
          return onTypeAhead(event.key);
        }
        return;
    }
  }, [isOpen, focusLocation, sections, isRTL, onDismiss, onNext, onPrevious, onHome, onEnd, onDrillDown, onDrillUp, onTypeAhead, earcon]);
  return {
    focusLocation,
    showFocus,
    onKeyDown,
    onItemHovered,
    onItemLeaved,
    onSubmenuClosed
  };
}
function useFocusControls(sections) {
  const [focusLocation, setFocusLocation] = useState(null);
  // We have visible focus outlines only if the user is navigating via the keyboard
  const [showFocus, setShowFocus] = useState(false);
  const earcon = useEarcon({
    source: EarconName.Error
  });
  const onItemHovered = useCallback(location => {
    if (!isValidLocation(location, sections)) return;
    setShowFocus(false);
    setFocusLocation(location);
  }, [sections]);
  const onItemLeaved = useCallback(location => {
    if (!location?.length || !focusLocation?.length || !isValidLocation(location, sections)) return;
    if (showFocus ||
    // no longer the hover state, shoule be move focus after hover
    canDrillDown(location, sections) ||
    // have submenu
    !location.some((_location, index) => _location[0] !== focusLocation[index][0] || _location[1] !== focusLocation[index][0]) // must be initial hovered item
    ) return;
    setFocusLocation(focusLocation.slice(0, -1));
  }, [showFocus, focusLocation, sections]);
  const onFocusChange = useCallback(location => {
    if (!isValidLocation(location, sections)) return;
    setShowFocus(true);
    setFocusLocation(location);
  }, [sections]);
  const onDrillUp = useCallback(() => {
    if (!focusLocation || !canDrillUp(focusLocation)) return earcon?.play();
    onFocusChange(focusLocation.slice(0, focusLocation.length - 1));
  }, [focusLocation, onFocusChange, earcon]);
  const onDrillDown = useCallback(() => {
    if (!focusLocation || !canDrillDown(focusLocation, sections)) return earcon?.play();
    onFocusChange([...focusLocation, [0, 0]]);
  }, [focusLocation, sections, onFocusChange, earcon]);
  const onNext = useCallback(start => {
    const currentSections = getCurrentSections(focusLocation, sections);

    // This is an empty context menu; do not focus anything
    if (currentSections.length === 0) return;
    const init = focusLocation ? focusLocation.slice(0, focusLocation.length - 1) : [];
    const currentLocation = focusLocation?.[focusLocation.length - 1];
    const [sectionIndex, itemIndex = -1] = start || !currentLocation ? [-1] : currentLocation;
    const currentSection = currentSections[sectionIndex];
    let last;
    if (currentSection && (isCustomSection(currentSection) && currentSection.focusableCount && currentSection.focusableCount > itemIndex + 1 || !isCustomSection(currentSection) && currentSection.items.length > itemIndex + 1)) {
      // Go to the next item in the current section
      last = [sectionIndex, itemIndex + 1];
    } else {
      // Go to the first item in the next section
      // If nothing currently has focus, focus the first item
      // Loop around to the first item when at end
      last = getNextFocusableLocation(sectionIndex, currentSections);
    }
    if (last) onFocusChange([...init, last]);
  }, [focusLocation, sections, onFocusChange]);
  const onPrevious = useCallback(end => {
    const currentSections = getCurrentSections(focusLocation, sections);

    // This is an empty context menu; do not focus anything
    if (currentSections.length === 0) return;
    const init = focusLocation ? focusLocation.slice(0, focusLocation.length - 1) : [];
    const currentLocation = focusLocation?.[focusLocation.length - 1];
    const [sectionIndex, itemIndex = -1] = end || !currentLocation ? [currentSections.length] : currentLocation;
    const currentSection = currentSections[sectionIndex];
    let last;
    if (itemIndex > 0 && currentSection && (isCustomSection(currentSection) && currentSection.focusableCount && currentSection.focusableCount > 0 || !isCustomSection(currentSection))) {
      // Go to the previous item in the current section
      last = [sectionIndex, itemIndex - 1];
    } else {
      // Go to the last item in the previous section
      // If nothing currently has focus, focus the last item
      // Loop around to the last item when at start
      last = getNextFocusableLocation(sectionIndex, currentSections, true);
    }
    if (last) onFocusChange([...init, last]);
  }, [focusLocation, sections, onFocusChange]);
  const onSubmenuClosed = useCallback(level => setFocusLocation(focusLocation => {
    // if hover/leave, showFocus is false, otherwise true
    // if the submenu was opened via hovering, when it needs to be closed (onSubmenuClosed), its parent menu item also needs to be removed from the focusLocation.
    const currentMenuLevel = (level ?? 0) + (showFocus ? 1 : 0);
    const focusLocationLength = focusLocation?.length ?? 0;
    if (currentMenuLevel <= focusLocationLength) {
      const location = focusLocation ? focusLocation.slice(0, currentMenuLevel) : [];
      if (isValidLocation(location, sections)) {
        return location;
      }
    }
    return focusLocation;
  }), [sections, showFocus]);
  const onTypeAhead = useCallback(key => {
    const currentSections = getCurrentSections(focusLocation, sections);

    // This is an empty context menu; do not focus anything
    if (currentSections.length === 0) return;
    const init = focusLocation ? focusLocation.slice(0, focusLocation.length - 1) : [];
    const currentLocation = focusLocation?.[focusLocation.length - 1];
    let nextFocusLocation;
    let hitted = false;
    for (let i = 0; i < currentSections.length; i++) {
      const section = currentSections[i];
      if (!isCustomSection(section)) {
        const items = section.items;
        for (let j = 0; j < items.length; j++) {
          const element = items[j];
          if (element.text.toLowerCase().startsWith(key.toLowerCase())) {
            const location = [i, j];
            // find the 1st item matched and value it to nextFocusLocation.
            nextFocusLocation = nextFocusLocation ?? location;
            if (currentLocation) {
              const sectionIndex = currentLocation[0];
              const itemIndex = currentLocation[1];

              // currentLocation exists, and find the target item below the focus in the current or the next section.
              if (i === sectionIndex && j > itemIndex || i > sectionIndex) {
                nextFocusLocation = location;
                hitted = true;
                break;
              }
            } else {
              // it is for the init time to open the menu, and break the loop.
              hitted = true;
              break;
            }
          }
        }
      }
      if (hitted) break;
    }
    if (nextFocusLocation) onFocusChange([...init, nextFocusLocation]);else earcon?.play();
  }, [focusLocation, sections, onFocusChange, earcon]);
  const onHome = useCallback(() => onNext(true), [onNext]);
  const onEnd = useCallback(() => onPrevious(true), [onPrevious]);
  const forceShowFocus = useCallback(() => setShowFocus(true), []);
  useEffect(() => {
    if (!focusLocation) return;
    let targetSections;
    const length = focusLocation.length;
    for (let i = 0; i < length; i++) {
      const index = focusLocation[i];
      const sectionIndex = index[0];
      const itemIndex = index[1];
      targetSections = targetSections ? targetSections : sections;
      const section = targetSections[sectionIndex];
      if (section) {
        if (isCustomSection(section)) return;
        const items = section.items;
        const item = items[itemIndex];
        if (item) {
          // if the last focusLocation item, and return
          if (i < length - 1) targetSections = item.subSections;
        } else if (itemIndex === items.length) {
          // move the focus to the next item
          return onNext();
        }
      } else {
        // when the section doesn't exit, the focusLocation length only has 2 types of values: 1 or greater than 1
        if (targetSections.length === 0) {
          return i === 0 ? setFocusLocation(null) : onDrillUp();
        }
        return onNext();
      }
    }
  }, [focusLocation, sections, onNext, onDrillUp]);
  return {
    focusLocation,
    showFocus,
    onItemHovered,
    onItemLeaved,
    onDrillUp,
    onDrillDown,
    onNext,
    onPrevious,
    onHome,
    onEnd,
    onSubmenuClosed,
    forceShowFocus,
    onTypeAhead
  };
}
function getCurrentSections(focusLocation, sections) {
  if (!focusLocation) return sections;
  let currentItem = null;
  let currentSections = sections;
  for (const [sectionIndex, itemIndex] of focusLocation) {
    if (currentItem) {
      if (!currentItem.subSections) break;
      currentSections = currentItem.subSections;
    }
    const section = currentSections?.[sectionIndex];
    if (!section) break;
    if (isCustomSection(section) || itemIndex === undefined) break;
    currentItem = section?.items[itemIndex]; // TODO safety
    if (!currentItem || !currentItem.subSections) break;
  }
  return currentSections;
}
function getFocusedItem(focusLocation, sections) {
  const currentSections = getCurrentSections(focusLocation, sections);
  const [sectionIndex, itemIndex] = focusLocation[focusLocation.length - 1];
  if (sectionIndex === undefined || itemIndex === undefined) return undefined;
  const currentSection = currentSections[sectionIndex];
  if (isCustomSection(currentSection)) return undefined;
  return currentSection.items[itemIndex];
}
function canDrillDown(focusLocation, sections) {
  const item = getFocusedItem(focusLocation, sections);
  return !!item && !!item.subSections && item.subSections.length > 0;
}
function canDrillUp(focusLocation) {
  return focusLocation && focusLocation.length > 1;
}
function isValidLocation(focusLocation, sections) {
  if (!focusLocation?.length) return true;
  const [sectionIndex, itemIndex] = focusLocation[focusLocation.length - 1];
  // The section and item index should exist
  if (sectionIndex === undefined || itemIndex === undefined) return false;
  const currentSections = getCurrentSections(focusLocation, sections);
  const currentSection = currentSections[sectionIndex];
  if (isCustomSection(currentSection)) return !!currentSection.focusableCount && currentSection.focusableCount > itemIndex;
  return !!currentSection.items[itemIndex];
}
export function getNextFocusableLocation(startIndex, sections, reverse) {
  let i = startIndex;
  do {
    // loop around
    if (!reverse && ++i >= sections.length) i = 0;else if (reverse && --i < 0) i = sections.length - 1;
    const section = sections[i];
    if (isCustomSection(section)) {
      if (section.focusableCount && section.focusableCount > 0) return [i, reverse ? section.focusableCount - 1 : 0];
    } else {
      return [i, reverse ? section.items.length - 1 : 0];
    }
  } while (i !== startIndex);
}
//# sourceMappingURL=useFocusNavigation.js.map